import BaseObject from '../../../base/base_object';
import {TimeSpan} from "@sparkware/app-utils";
import SessionTimerLogicHelper from "../helpers/session_timer_logic_helper";
import {CLOCK_LOCALIZATION} from "../../clock/clock_service_consts";
import ServiceFactory from '../../service_factory';
import GenericRealityCheckMessageHandler from '../helpers/generic_reality_check_message_handler';
import {GNWP_SOURCES} from "../../../../../app-react/services/gnwp/gnwp-consts";
import { GameChildishIconMode } from '../../../../../common/enums/game-childish-icon-mode.enum';
import { REGULATION_STATUS_ID_MAP } from '../regulation_const';

export enum StartSessionTimerContext {
    OnAppStartup,
    OnLogin
}

export interface IAccountData {
    RegulationStatusId: number,
    IdVerificationStatus: number
}

export interface BaseRegulationParams {
    serviceFactory: ServiceFactory,
    cssSuffix: string,
    realityCheckInterval: string
}

export default class BaseRegulation extends BaseObject{

    static CLASS = 'BaseRegulation';

    protected init(params: BaseRegulationParams) {
        super.init(params);
        this.serviceFactory = params.serviceFactory;
        this.cssSuffix = params.cssSuffix;
        this._realityCheckInterval = this._parseRealityCheckInterval(params.realityCheckInterval);
    }

    protected serviceFactory: ServiceFactory;
    private cssSuffix: string;
    private _sessionTimerHandler: SessionTimerLogicHelper = null;
    private _realityCheckMessageHandler: GenericRealityCheckMessageHandler | null = null;

    private _parseRealityCheckInterval(interval) {
        let defaultValue = TimeSpan.fromHours(1);
        if(!interval) {
            return defaultValue;
        }
        try {
            let timeSpan = TimeSpan.parse(interval);
            timeSpan = new TimeSpan(timeSpan.days, timeSpan.hours, timeSpan.minutes, 0, 0);
            if (timeSpan.totalMinutes < 1) {
                return TimeSpan.fromMinutes(1);
            }

            return timeSpan;
        }
        catch (err) {
            this.logger.error('Failed to parse RealityCheckInterval ' + interval);
            return defaultValue;
        }
    }

    private _realityCheckInterval: TimeSpan;
    getRealityCheckInterval(): TimeSpan {
        if(this.realityCheckEnabled()) {
            return this._realityCheckInterval;
        } else {
            return TimeSpan.fromMilliseconds(0);
        }
    }

    updateRealityCheckInterval(realityCheckIntevalMin: number): Promise<void> {
        /**
         * Normally, the user cannot modify or update the reality check interval
         * This method is implemented in Malta and UK Regulations, regulations that allow the user to set a desired interval
         */
        return Promise.resolve();
    }

    hybridNyxClosedButtonEnabled(){
        return false;
    }


    forceHideCloseButtonInMembership() {
        return false;
    }

    getRealityCheckIntervalExpectedByTheGame() {
        let interval = this.getRealityCheckInterval();
        return {
            timeSpan: interval,
            expectedValueByTheGame: Math.round(interval.totalMinutes)
        }
    }

    dispose() {
        if (this._realityCheckMessageHandler) {
            this._realityCheckMessageHandler.dispose();
        }

        if (this._sessionTimerHandler) {
            this._sessionTimerHandler.dispose();
        }
    }

    protected _getRealityCheckMessageHandler(context: StartSessionTimerContext): GenericRealityCheckMessageHandler | null {
        if (!this.realityCheckEnabled()) {
            return null;
        }

        return GenericRealityCheckMessageHandler.create({
            serviceFactory: this.serviceFactory,
            realityCheckInterval: this.getRealityCheckInterval(),
            handleRealityCheckMessageCallback: this._handleRealityCheckMessage.bind(this),
            context: context
        });
    }

    protected _handleRealityCheckMessage(sessionTimer: TimeSpan, confirmCallback?:Function): void {

    }

    showSessionBalanceInAboutBox(): boolean {
        return false;
    }

    showTempAccountMessage(): void {
    }

    protected sessionTimerEnabled(): boolean {
        return false;
    }

    sessionTimerVisible(): boolean {
        return false;
    }

    protected realityCheckEnabled(): boolean {
        return false;
    }

    showResponsibleIcons(): boolean {
        return false;
    }

    showAgeResponsibleGameInDetailedHistory(): boolean {
        return false;
    }

    showAgeResponsibleIconInGame(): boolean {
        return true;
    }

    getCurrentSessionTime(): TimeSpan {
        if (this._sessionTimerHandler) {
            return this._sessionTimerHandler.getSessionTime();
        }
        return TimeSpan.fromMinutes(0);
    }


    getCssSuffix(): string {
        return this.cssSuffix;
    }

    startSessionTimer(context: StartSessionTimerContext): void {
        if (this.sessionTimerEnabled()) {

            if(!this._sessionTimerHandler) {
                this._sessionTimerHandler = SessionTimerLogicHelper.create();
            }

            if(this._realityCheckMessageHandler) {
                this._realityCheckMessageHandler.dispose();
            }

            this._realityCheckMessageHandler = this._getRealityCheckMessageHandler(context)


            this._sessionTimerHandler.start();
        }
    }


    stopSessionTimer(): void {
        if(this._realityCheckMessageHandler) {
            this._realityCheckMessageHandler.dispose();
        }

        if(this._sessionTimerHandler) {
            this._sessionTimerHandler.dispose();
            this._sessionTimerHandler = null;
        }
    }

    onKickEvent(kickParams): void {

    }


    openLimits(): void {

    }

    showLoginMessage(): Promise<void> {
        return Promise.resolve();
    }

    showLogoutMessage(): void {}

    getClockLocalization(): string {
        return CLOCK_LOCALIZATION.LOCAL;
    }

    getErrorMessage(errorCode: number): string {
        return this.serviceFactory.languageService().translation(errorCode.toString());
    }

    isAnonymousPlayAllowed(): boolean {
        return true;
    }

    forceDemoButton(): boolean {
        return false;
    }

    forceIntermediatePageForAutorizedUsers(): boolean {
        return true;
    }

    supportWithdrawOnlyFromReal(): boolean {
        return true;
    }

    shouldPreventMoreThanOneOpenGame(): boolean {
        return true;
    }

    getTempAccountGnwpSource() {
        return GNWP_SOURCES.CASHIER;
    }

    postLoginActionsArePassed() {
        return true;
    }

    showHelpInAboutWindow() {
        return true;
    }

    isSessionCooldownEnabled(): boolean {
        return false;
    }

    showNetPosition(): boolean {
        return false;
    }

    overrideCurrencySymbol(): null | string {
        return null;
    }

    displayPopUpRatingAfterGame(): boolean {
        return true;
    }

    displayPopUpRatingAfterLogin(): boolean {
        return true;
    }


    shouldShowPlayerLimitsToaster(): boolean {
        return false;
    }

    checkIfCorrectAppInUse(country:string, state: string) {

    }

    shouldCallCountryByIP(country:string) {
        return false;
    }

    runConditionsBeforeOpenLink(source: string):Promise<void> {
        return Promise.resolve();
    }

    fitToPlayModeEnabled(): boolean {
        return false;
    }

    stepsBeforeOpenLoginAndRegistration():Promise<void> {
        return Promise.resolve();
    }

    getAgeResponsibleGamingLabel(): string | null {
        return null;
    }

    shouldRefreshTokenOnAppOpen() {
        return true;
    }

    showNightLimitMessage(nightLimitExpirationDate: Date) {

    }

    isLoginPopupEnabled(): boolean {
        return false;
    }

    isDeviceDetectionEnabled() : boolean {
        return false;
    }

    gameIconAnimationsAllowed() {
        return true;
    }

    protected isVerifiedForRegulation(): boolean {
        const userAccountData = this.serviceFactory.userService().getAccountData() as IAccountData;
       
        return userAccountData.RegulationStatusId === REGULATION_STATUS_ID_MAP.APPROVED_FOR_REGULATION;
    }
    
    getChildishIconMode(): GameChildishIconMode {
        return GameChildishIconMode.UseDefaultIcon;
    }
}